import type { TimeSlot } from "@/features/availability/types/availabilityTypes";

const incrementMs = 15 * 60_000;
const alignStart = (earliest: number, dayStart: number) =>
  dayStart + Math.ceil((earliest - dayStart) / incrementMs) * incrementMs;

export function mergeAvailabilityWindows(timeSlots: TimeSlot[]) {
  const windows: { start: number; end: number }[] = [];
  for (const window of timeSlots.map((slot) => ({ start: new Date(slot.start).getTime(), end: new Date(slot.end).getTime() }))
    .filter((slot) => Number.isFinite(slot.start) && Number.isFinite(slot.end) && slot.end > slot.start)
    .sort((a, b) => a.start - b.start)) {
    const previous = windows[windows.length - 1];
    if (previous && window.start <= previous.end) previous.end = Math.max(previous.end, window.end);
    else windows.push({ ...window });
  }
  return windows;
}

export function generateAvailabilityGaps(timeSlots: TimeSlot[], startAt: Date, endAt: Date, now = new Date()) {
  return mergeAvailabilityWindows(timeSlots).flatMap((window) => {
    const start = alignStart(Math.max(window.start, startAt.getTime(), now.getTime() + 1), startAt.getTime());
    const end = Math.min(window.end, endAt.getTime());
    return end - start >= 60_000 ? [{ startAt: new Date(start), endAt: new Date(end) }] : [];
  });
}

export const generateAvailabilityTimeSlots = (
  timeSlots: TimeSlot[],
  startAt: Date,
  endAt: Date,
  slotDurationMinutes: number,
  bufferBeforeMinutes = 0,
  bufferAfterMinutes = 0,
): { startAt: Date; endAt: Date }[] => {
  const slots: { startAt: Date; endAt: Date }[] = [];
  if (!Number.isFinite(slotDurationMinutes) || slotDurationMinutes <= 0 ||
    !Number.isFinite(bufferBeforeMinutes) || bufferBeforeMinutes < 0 ||
    !Number.isFinite(bufferAfterMinutes) || bufferAfterMinutes < 0 ||
    !Number.isFinite(startAt.getTime()) || !Number.isFinite(endAt.getTime()) || endAt <= startAt) return slots;
  const slotDurationMs = slotDurationMinutes * 60 * 1000;
  const seen = new Set<number>();

  for (const timeSlot of mergeAvailabilityWindows(timeSlots)) {
    const slotStart = new Date(timeSlot.start);
    const slotEnd = new Date(timeSlot.end);
    const earliest = Math.max(slotStart.getTime() + bufferBeforeMinutes * 60_000, startAt.getTime());
    let currentStart = new Date(alignStart(earliest, startAt.getTime()));

    while (currentStart < slotEnd && currentStart < endAt) {
      const currentEnd = new Date(currentStart.getTime() + slotDurationMs);
      if (currentEnd.getTime() + bufferAfterMinutes * 60_000 <= slotEnd.getTime() &&
        currentEnd <= endAt && !seen.has(currentStart.getTime())) {
        seen.add(currentStart.getTime());
        slots.push({
          startAt: new Date(currentStart),
          endAt: new Date(currentEnd),
        });
      }
      // Move to next 15-minute interval instead of full duration
      currentStart = new Date(currentStart.getTime() + incrementMs);
    }
  }

  return slots.sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
};
